import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Star, Globe2, Package, Hash, ChevronRight, Heart, Share2, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../hooks/useSettings';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/clipboard';
import { API_BASE } from '../../utils/apiConfig';

const FeaturedProductCard = ({ product, index, imageHeight = '160px' }) => {
  const navigate = useNavigate();
  const { currencySymbol, convertPrice } = useSettings();
  const { addToCart } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [isAdded, setIsAdded] = useState(false);

  const getImageUrl = (imgPath, width) => {
    const path = Array.isArray(imgPath) ? imgPath[0] : imgPath;
    if (!path) return "https://www.rallis.com/Upload/Images/thumbnail/Product-inside.png";
    if (path.startsWith('http')) return path;

    // /uploads → backend; pass a width for a compressed, resized WebP.
    if (path.startsWith('/uploads')) {
      return `${API_BASE}${path}${width ? `?w=${width}` : ''}`;
    }

    if (path.startsWith('/')) return path;
    return `/${path}`;
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (isAdded) return;
    addToCart(product);
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    toggleFavorite(product);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const slug = product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const shareUrl = `${window.location.origin}/products/${product.id || product.p_id}-${slug}`;
    if (await copyToClipboard(shareUrl)) {
      toast.success("Link copied to clipboard!", {
        icon: <Share2 className="text-orange-600" size={16} />
      });
    } else {
      toast.error("Couldn't copy automatically", { description: shareUrl });
    }
  };

  // Map product data
  const _meta = typeof product.metadata === 'string' ? (() => { try { return JSON.parse(product.metadata); } catch { return {}; } })() : (product.metadata || {});
  // When manual rating is turned off in the panel, show the live numbers
  // derived from the actual reviews added to the product.
  const _reviews = (() => {
    const r = product.review;
    if (Array.isArray(r)) return r;
    if (typeof r === 'string') { try { return JSON.parse(r); } catch { return []; } }
    return [];
  })();
  const _liveCount = Array.isArray(_reviews) ? _reviews.length : 0;
  const _liveAvg = _liveCount ? (_reviews.reduce((a, r) => a + (parseFloat(r.rating) || 0), 0) / _liveCount) : 0;
  const _manual = _meta.rating_manual !== false;
  const ratingData = _manual ? (_meta.rating_value || product.rating || null) : (_liveCount ? _liveAvg.toFixed(1) : null);
  const reviewCount = _manual ? (_meta.rating_count || product.review_count || null) : (_liveCount || null);
  const rawPrice = (product.p_1 || product.main_p || product.price || "0").toString();
  const fmt = (n) => convertPrice(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const displayPrice = fmt(rawPrice);

  // Lowest per-piece price across all tiers
  const tierPriceKeys = ['p_1','p_2','p_3','p_4','p_5','p_6','p_7','p_8','p_9','p_10'];
  const tierLabelKeys = ['p1q','p2q','p3q','p4q','p5q','p6q','p7q','p8q','p9q','p10q'];
  const toNum2 = (v) => parseFloat((v ?? '').toString().replace(/[^0-9.]/g, '')) || 0;
  let _lowestPcs = Infinity; let _hasMultiTier = false;
  tierPriceKeys.forEach((pk, i) => {
    const tierPrice = toNum2(product[pk]);
    if (!tierPrice) return;
    const lbl = (product[tierLabelKeys[i]] || '').toString();
    const qty = parseInt(lbl.replace(/[^0-9]/g, '')) || 1;
    if (qty > 1) _hasMultiTier = true;
    if (tierPrice < _lowestPcs) _lowestPcs = tierPrice;
  });
  const showFrom = _hasMultiTier && isFinite(_lowestPcs) && _lowestPcs < (toNum2(rawPrice));

  // Discount: original (M.R.P) is product.main_p, falling back to
  // metadata.original_price — same source as the product detail page.
  const toNum = (v) => parseFloat((v ?? '').toString().replace(/[^0-9.]/g, '')) || 0;
  const _origRaw = product.main_p || _meta.original_price || '';
  const displayOriginal = fmt(_origRaw);
  const _priceNum = toNum(rawPrice);
  const _origNum = toNum(_origRaw);
  const hasDiscount = _origNum > _priceNum && _priceNum > 0;
  const adminDiscount = Number(product.discount_percent) || 0;
  const discountPct = adminDiscount > 0 ? adminDiscount : (hasDiscount ? Math.round(((_origNum - _priceNum) / _origNum) * 100) : 0);

  const formatCategory = (val) => {
    if (!val) return "Premium Catalog";
    if (typeof val === 'string' && val.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) {
          return parsed[0] || "Premium Catalog";
        }
      } catch (e) {
        console.error("JSON Parse Error in FeaturedProductCard:", e);
      }
    }
    return val;
  };

  const categoryDisplay = formatCategory(product.sub_category_name || product.category_name || product.category);

  return (
    <motion.div
      onClick={() => navigate(`/products/${product.id || product.p_id}`)}
      className="bg-white rounded-2xl px-2 pt-2 pb-2 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-50 flex flex-col group cursor-pointer w-full overflow-hidden h-full"
      style={{ touchAction: 'manipulation' }}
    >
      {/* Product Image Container */}
      <div className="relative rounded-xl overflow-hidden mb-2 font-sans shrink-0 bg-white" style={{ height: imageHeight }}>
        <img
          src={getImageUrl(product.image_url || product.imageUrl, 500)}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
          onError={e => { e.target.onerror = null; e.target.src = "https://www.rallis.com/Upload/Images/thumbnail/Product-inside.png"; }}
        />

        {/* Action Buttons - Hidden by default, show on hover */}
        <div className="absolute top-2 right-2 z-30 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-4 group-hover:translate-x-0">
          <button
            onClick={handleFavoriteClick}
            className="w-6 h-6 rounded-full flex items-center justify-center bg-white/90 backdrop-blur-md shadow-lg border border-slate-100 hover:scale-110 active:scale-95 transition-all"
          >
            <Heart
              size={12}
              className={`${isFavorite(product.id || product.p_id) ? 'fill-red-500 text-red-500' : 'text-slate-400'}`}
            />
          </button>
          <button
            onClick={handleShare}
            className="w-6 h-6 rounded-full flex items-center justify-center bg-white/90 backdrop-blur-md shadow-lg border border-slate-100 hover:scale-110 active:scale-95 transition-all"
          >
            <Share2 size={12} className="text-slate-400" />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-col gap-1.5 flex-1">
        <div className="space-y-0.5">
          <h5 className="text-sm font-black text-slate-900 leading-tight group-hover:text-[#FF6B35] transition-all duration-300 line-clamp-2 min-h-[2.5em]">
            {product.name}
          </h5>

          {/* Rating */}
          <div className="flex items-center gap-1">
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={8} className={ratingData && s <= Math.floor(ratingData) ? "fill-orange-400 text-orange-400" : "text-slate-200"} />
              ))}
            </div>
            {ratingData && <span className="text-[10px] font-black text-slate-900">{ratingData}</span>}
            {reviewCount && <span className="text-[8px] font-bold text-slate-300">({reviewCount})</span>}
          </div>
        </div>


        {/* Bottom Section */}
        <div>
          <div className="mb-1">
            <div className="flex items-center gap-1 whitespace-nowrap overflow-hidden">
              {showFrom && <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">From</span>}
              <span className="text-base font-black text-slate-900 tracking-tight">
                {currencySymbol}{showFrom ? fmt(_lowestPcs.toString()) : displayPrice}{showFrom && <span className="text-[10px] font-bold text-slate-500">/pcs</span>}
              </span>
              {!showFrom && hasDiscount && (
                <span className="text-[10px] font-semibold text-slate-400 line-through">{currencySymbol}{displayOriginal}</span>
              )}
            </div>
            <div className="h-[18px] flex items-center">
              {!showFrom && hasDiscount && discountPct > 0 && (
                <span className="text-[8px] font-bold text-orange-700 bg-orange-50 px-1 py-0.5 rounded">{discountPct}% OFF</span>
              )}
            </div>
          </div>
          {product.status === 'Out of Stock' ? (
            <button disabled className="w-full py-2 rounded-xl flex items-center justify-center gap-2 bg-slate-200 text-slate-400 text-xs font-bold cursor-not-allowed">
              Out of Stock
            </button>
          ) : (
            <button
              onClick={handleAddToCart}
              className={`w-full py-2 rounded-xl flex items-center justify-center gap-2 transition-all shadow-md transform active:scale-95 text-white text-xs font-bold ${isAdded ? 'bg-orange-500' : 'bg-[#0f172a] hover:bg-slate-800'}`}
            >
              {isAdded ? (
                <><Check size={12} strokeWidth={3} /> Added</>
              ) : (
                <><ShoppingCart size={12} strokeWidth={2.5} /> Add to Cart</>
              )}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default FeaturedProductCard;
